import { ViewportScroller } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { States } from 'app/_models/constants';
import { PermitsService } from 'app/forests/services/permits.service';
import { WindowRef } from 'app/_services/native-window.service';
import CustomValidators from 'app/shared/custom-validators';
import { FeedbackService } from 'app/_services/feedback.service';

@Component({
    selector: 'app-faq',
    templateUrl: './faq.component.html',
    styleUrls: ['./faq.component.scss'],
    standalone: false
})
export class FaqComponent implements OnInit {
  isLoading = false;
  faqList = [{
    title: 'What is a special forest product?',
    content: `Any product collected from national forests and grasslands are considered special forest products, including but not limited to: bark, berries, boughs, bryophytes, bulbs, burls, Christmas trees, cones, ferns, firewood, forbs, fungi (including mushrooms), grasses, mosses, nuts, pine straw, roots, sedges, seeds, transplants, tree sap, wildflowers, fence material, mine props, posts and poles, shingle and shake bolts, and rails.
    <br>
    <br>
    Special forest products do not include: sawtimber, pulpwood, non-sawlog material removed in log form, cull logs, small roundwood, house logs, telephone poles, derrick poles, minerals, animals, animal parts, insects, worms, rocks, water, and soil.
    `
  }, {
    title: 'Why do I need a permit to harvest special forest products?',
    content: `All special forest products require a permit for removal from the forest, unless specifically exempted.  Permits help ensure proper harvesting techniques are used to protect forest resources.`
  }, {
    title: 'How do I know if the forest near me offers permits for special forest products online through E-Permits?',
    content: 'On the home page of the E-Permit site you can view a drop-down list of the forests that have permits available online.  If your local forest does not appear in this drop-down list, then permits are available at your local Forest Service office or participating vendor.  If you are still unsure whether your local forest offers online firewood permits, please call your local Forest Service office.'
  }, {
    title: 'Why are only firewood permits available through E-Permits?',
    content: 'As we begin using this new system, firewood is the only product available in the E-Permit system.  Additional products will be added in the future.  If permits for the product you are looking for are not available, please visit or contact your local Forest Service office. '
  }, {
    title: 'How long will it take me to get a permit using the E-Permit site?',
    content: 'From start to finish, it should take you approximately 10-15 minutes to obtain your permit.'
  }, {
    title: 'What do I need to prepare to get a permit online using E-Permits?',
    content: 'To successfully obtain a permit online using the E-Permit site, you will need to have a valid email address, state or Federal ID, Debit, Credit Card or Bank Account and a black and white printer.'
  }, {
    title: 'What if I don’t have a printer?',
    content: 'If you do not have a printer available, you will need to get your permit from a local Forest Service office or participating vendor location.  Permits must be printed on paper to be valid.'
  }, {
    title: 'Can I get a permit from the E-Permit site using my mobile phone?',
    content: 'Yes.  E-Permits may be used on desktop computers and mobile devices. However, permits must be printed on paper and carried to the field to be valid.'
  }, {
    title: 'Are digital/mobile versions of the permit acceptable?',
    content: 'No. Digital versions of permits are not accepted.  You must have a printed copy of the permit along with a valid ID when harvesting to ensure proper compliance with the permitting requirements.'
  }, {
    title: 'Is the E-Permit system secure?',
    content: `Yes.  The Forest Service has followed all agency, USDA, and federal cybersecurity protocols while building and implementing E-Permits.  Identity and payment information will be protected through Pay.gov.  Additional information on Pay.gov’s Privacy and Security can be found <a href="https://www.pay.gov/public/about-us/privacy-and-security" target="_blank">here</a>.`
  }, {
    title: 'Will personal information be protected?',
    content: `Personal and financial information entered into the E-Permit system is secure and protected through Pay.gov. For more information on Pay.gov you can visit their <a href="https://www.pay.gov/public/home" target="_blank">website</a>.`
  }, {
    title: 'What is Pay.gov and how safe is it? ',
    content: `Pay.gov is a free and secure service that allows you to pay United States Government agencies.
    <br>
    <br>
    Visit Pay.gov for more answers: <a href="https://www.pay.gov/public/home" target="_blank">Pay.gov</a>.`
  }, {
    title: 'Is my permit transferable? Example: Can I get a permit in my name and ask someone else to harvest the products for me?',
    content: `All permits are non-transferable, meaning your permit cannot be given to, or used by another person.  However, if you are in attendance during the harvest activity you are not required to participate in the harvest activity granted by your permit.
    <br>
    <br>
    Example: You can have a family member or friend do the harvest activity for you if you are in attendance at all times during the harvesting and transportation of the harvested product.
    <br>
    <br>
    If you would like to designate a second cutter, contact your local office. This option may not be available for all special forest products and at all locations.
    `
  }, {
    title: 'What do I do if I did not receive my permit confirmation email?',
    content: `If you did not receive your confirmation email after purchasing your permit, please contact your local office for assistance. Do not purchase an additional permit.`
  }, {
    title: 'What do I do if I lose my permit and other associated documentation?',
    content: `If you lose your permit, please contact the local forest where your permit is valid. Forest Service staff will help you retrieve your permit or obtain a new one.`
  }, {
    title: 'Can I get a refund if I paid for my permit and did not use it or lost it?',
    content: `Once you have paid for your permit, the sale is considered final, and payments are not subject to a refund. If you lost your permit, you may contact your local Forest Service office for assistance.`
  }, {
    title: 'If I do not want to use E-Permits to obtain my permit, will I still be able to get a permit in person at local Forest Service offices?',
    content: `Yes.  Firewood permits can still be obtained in-person at a Forest Service office or through local participating vendors.`
  }, {
    title: 'Who do I contact if I have issues obtaining a permit online using E-Permits? ',
    content: `If you have issues using the E-Permits site you can contact your local Forest Service office.  If the local staff are unable to answer your questions, they will pass it along to the E-Permit team to be addressed in a timely manner.`
  }];

  constructor(
    private readonly viewPortScroller: ViewportScroller,
    public applicationService: PermitsService
  ) {
  }

  ngOnInit() {
    this.viewPortScroller.scrollToPosition([0, 0]);
  }

  setOpen(element: any) {
    let isOpen = element.isOpen;
    this.faqList.forEach((faq: any) => faq.isOpen = false);
    element.isOpen = !isOpen;
  }

}
